<template>
  <div>
    <DxForm
      id="formularioComunicado"
      ref="formInstance"
      :form-data="model"
      label-location="top"
      :show-colon-after-label="false"
      :visible="model != null"
      validation-group="validacionComunicado"
    >
      <DxColCountByScreen :xs="6" :sm="6" :md="6" :lg="6" />

      <DxGroupItem :col-span="6" :col-count="6">
        <DxSimpleItem data-field="Tipo" editor-type="dxSelectBox" :col-span="3" :editor-options="selectTipoComunicadoOptions">
          <DxLabel :text="$t('comunicados.form.tipo_via_comunicacion')" />
          <DxRequiredRule :message="$t('comunicados.form.validacion.tipo_via_comunicacion')" />
        </DxSimpleItem>
        <DxEmptyItem :col-span="3"> </DxEmptyItem>
        <DxSimpleItem data-field="TipoEmpresaAgenteDirigido" editor-type="dxSelectBox" :col-span="3" :editor-options="selectTipoEmpresaAgenteDirigidoOptions">
          <DxLabel :text="$t('comunicados.form.tipo_empresa_agente_dirigido')" />
        </DxSimpleItem>
        <DxSimpleItem data-field="RolesDirigidos" editor-type="dxTagBox" :col-span="3" :editor-options="selectRolOptions">
          <DxLabel :text="$t('comunicados.form.roles')" />
        </DxSimpleItem>
        <DxSimpleItem
          data-field="EmpresasAgentesDirigidos"
          editor-type="dxTagBox"
          :col-span="6"
          :editor-options="selectEmpresaAgentesOptions"
        >
          <DxLabel :text="$t('comunicados.form.empresas_agentes')" />
        </DxSimpleItem>
        <DxSimpleItem data-field="Asunto" editor-type="dxTextBox" :col-span="6">
          <DxLabel :text="$t('comunicados.form.asunto')" />
          <DxRequiredRule :message="$t('comunicados.form.validacion.asunto')" />
        </DxSimpleItem>
        <DxSimpleItem :col-span="6">
          <DxHtmlEditor v-model="props.model!.Cuerpo" height="300">
            <DxToolbar :multiline="isMultiline">
              <DxItem name="undo" />
              <DxItem name="redo" />
              <DxItem name="separator" />
              <DxItem name="size" :accepted-values="sizeValues" :options="fontSizeOptions" />
              <DxItem name="font" :accepted-values="fontValues" :options="fontFamilyOptions" />
              <DxItem name="separator" />
              <DxItem name="bold" />
              <DxItem name="italic" />
              <DxItem name="strike" />
              <DxItem name="underline" />
              <DxItem name="separator" />
              <DxItem name="alignLeft" />
              <DxItem name="alignCenter" />
              <DxItem name="alignRight" />
              <DxItem name="alignJustify" />
              <DxItem name="separator" />
              <DxItem name="orderedList" />
              <DxItem name="bulletList" />
              <DxItem name="separator" />
              <DxItem name="color" />
              <DxItem name="background" />
              <DxItem name="separator" />
              <DxItem name="clear" />
            </DxToolbar>
            <DxValidator validation-group="validacionComunicado">
              <DxCustomRule :validation-callback="validarCuerpo" :message="$t('comunicados.form.validacion.mensaje')" :reevaluate="true" />
            </DxValidator>
          </DxHtmlEditor>
          <DxLabel :text="$t('comunicados.form.mensaje')" />
        </DxSimpleItem>
        <DxSimpleItem :col-span="6">
          <MultipleFileUploader
            :max-files="10"
            v-model:ficheros="ficheros"
            :textForAddAttachmentLabel="$t('tickets.form.adjuntos')"
            :textForAddAttachmentButton="$t('documento_card.selecciona_fichero')"
            :textForNonDownloadableFiles="$t('tickets.form.texto_adjunto_no_descargable')"
            mode="button-selector"
            @fileDownloadRequested="descargarAdjunto"
          >
          </MultipleFileUploader>
        </DxSimpleItem>
      </DxGroupItem>
    </DxForm>
  </div>
</template>

<script setup lang="ts">
import { messageBoxResult } from "@components/dialogs/message-box";
import { useModalBuilder, type ToolbarReference } from "@helpers/modal-building-helper";
import { cancelButtonProps, primaryButtonProps, saveButtonProps } from "@helpers/button-helper";
import { DxForm, DxLabel, DxColCountByScreen, DxRequiredRule, DxSimpleItem, DxGroupItem, DxEmptyItem } from "devextreme-vue/form";
import DxHtmlEditor, { DxToolbar, DxItem } from "devextreme-vue/html-editor";
import DxValidator, { DxCustomRule } from "devextreme-vue/validator";
import { type DxButton, type DxTagBox } from "devextreme-vue";
import { computed, getCurrentInstance, onBeforeMount, onMounted, ref, watch } from "vue";
import { useIntl } from "@services/i18n";
import validationEngine from "devextreme/ui/validation_engine";
import { ComunicadoModel, TIPO_EMPRESA_AGENTE_TODAS } from "@/components/comunicados/models/ComunicadoModel";
import MultipleFileUploader from "@/.shared/components/file-uploading/MultipleFileUploader.vue";
import { getSelectBoxDefaultProperties } from "@helpers/grid-helper";
import { getSelectBoxFromApiEnum, getTranslatedListApiEnum } from "@helpers/enums-helper";
import { type DetailAdjuntoComunicadoModel, type RolesRm, type SelectBoxGuidEntityModel, TipoComunicadoEnum, TipoEmpresaAgentesEnum } from "@api/api";
import { useApi } from "@api/integration";
import { toastError, toastSuccess } from "@/.shared/services/notification-service";
import FileSaver from "file-saver";

const ROLES_COLABORADORA = ["SUPERAGENTE", "AGENTE"] as const;
const ROLES_INMOBILIARIA = ["DIRECTOR_INMOBILIARIA", "COORDINADOR_DELEGACION_INMOBILIARIA", "AGENTE_INMOBILIARIA"] as const;
const ROLES_TODAS_EMPRESAS = [...ROLES_COLABORADORA, ...ROLES_INMOBILIARIA];

const intl = useIntl();
const api = useApi();
const formInstance = ref<DxForm>();
const ficheros = ref<Ficheros>({});
const idComunicadoCreado = ref<string | null>(null);
const todosLosRoles = ref<RolesRm[]>([]);
const rolesDisponibles = ref<RolesRm[]>([]);
const empresasAgentesDisponibles = ref<SelectBoxGuidEntityModel[]>([]);

const selectRolOptions = computed<Partial<DxTagBox>>(() => ({
  valueExpr: "Id",
  displayExpr: "Name",
  searchEnabled: true,
  placeholder: intl.i18n.t("comunicados.grid.todos"),
  dataSource: rolesDisponibles.value,
}));

const selectEmpresaAgentesOptions = computed<Partial<DxTagBox>>(() => ({
  ...getSelectBoxDefaultProperties() as Partial<DxTagBox>,
  placeholder: intl.i18n.t("comunicados.grid.todas"),
  dataSource: empresasAgentesDisponibles.value,
}));

const props = defineProps({
  model: {
    type: Object as () => ComunicadoModel,
    required: true,
  },
  isEditMode: {
    type: Boolean,
    required: true,
  },
});

const selectTipoComunicadoOptions = {
  ...getSelectBoxFromApiEnum(TipoComunicadoEnum),
  showClearButton: false,
  searchEnabled: false,
};

const selectTipoEmpresaAgenteDirigidoOptions = {
  ...getSelectBoxFromApiEnum(TipoEmpresaAgentesEnum),
  dataSource: [
    { value: TIPO_EMPRESA_AGENTE_TODAS, text: intl.i18n.t("comunicados.tipo_empresa_agente_dirigido.__TODOS__") },
    ...getTranslatedListApiEnum(TipoEmpresaAgentesEnum),
  ],
  showClearButton: false,
  searchEnabled: false,
};

const sizeValues = ["8pt", "10pt", "12pt", "14pt", "18pt", "24pt", "36pt"];
const fontValues = ["Arial", "Courier New", "Georgia", "Impact", "Lucida Console", "Tahoma", "Times New Roman", "Verdana"];
const isMultiline = ref(true);
const fontSizeOptions = { inputAttr: { "aria-label": "Font size" } };
const fontFamilyOptions = { inputAttr: { "aria-label": "Font family" } };

const getRolesPermitidos = (tipoEmpresa: string | null | undefined): string[] => {
  if (tipoEmpresa === TipoEmpresaAgentesEnum.Colaboradora) {
    return [...ROLES_COLABORADORA];
  }
  if (tipoEmpresa === TipoEmpresaAgentesEnum.Inmobiliaria) {
    return [...ROLES_INMOBILIARIA];
  }
  return ROLES_TODAS_EMPRESAS;
};

const getTiposEmpresaAgenteFiltro = (tipoEmpresa: string | null | undefined): string[] | undefined => {
  if (tipoEmpresa === TipoEmpresaAgentesEnum.Colaboradora) {
    return [TipoEmpresaAgentesEnum.Colaboradora];
  }
  if (tipoEmpresa === TipoEmpresaAgentesEnum.Inmobiliaria) {
    return [TipoEmpresaAgentesEnum.Inmobiliaria];
  }
  return undefined;
};

const actualizarRolesDisponibles = (tipoEmpresa: string | null | undefined) => {
  const normalizedNamesPermitidos = new Set(getRolesPermitidos(tipoEmpresa));
  rolesDisponibles.value = todosLosRoles.value.filter((rol) => normalizedNamesPermitidos.has(rol.NormalizedName));
  const idsDisponibles = new Set(rolesDisponibles.value.map((rol) => rol.Id));
  props.model.RolesDirigidos = props.model.RolesDirigidos.filter((id) => idsDisponibles.has(id));
};

const actualizarEmpresasAgentesDisponibles = async (tipoEmpresa: string | null | undefined) => {
  const tipos = getTiposEmpresaAgenteFiltro(tipoEmpresa);
  empresasAgentesDisponibles.value = tipos
    ? await api.callStandardApi("getEmpresaAgentesSelector", { showLoading: false }, tipos)
    : await api.callStandardApi("getEmpresaAgentesSelector", { showLoading: false });
  const idsDisponibles = new Set(empresasAgentesDisponibles.value.map((empresa) => empresa.Id));
  props.model.EmpresasAgentesDirigidos = props.model.EmpresasAgentesDirigidos.filter((id) => idsDisponibles.has(id));
};

const onTipoEmpresaAgenteDirigidoChanged = async (tipoEmpresa: string | null | undefined) => {
  actualizarRolesDisponibles(tipoEmpresa);
  await actualizarEmpresasAgentesDisponibles(tipoEmpresa);
};

onBeforeMount(async () => {
  if (props.isEditMode && props.model.Id) {
    idComunicadoCreado.value = props.model.Id;
    initFicherosFromAdjuntos(props.model.adjuntosIniciales);
  }

  todosLosRoles.value = await api.callStandardApi("getRolesSelector", { showLoading: false });
  await onTipoEmpresaAgenteDirigidoChanged(props.model.TipoEmpresaAgenteDirigido);
});

watch(
  () => props.model.TipoEmpresaAgenteDirigido,
  (tipoEmpresa) => {
    void onTipoEmpresaAgenteDirigidoChanged(tipoEmpresa);
  },
);

onMounted(() => {
  const modal = useModalBuilder();
  modal.setContext(getCurrentInstance()!);
  configureButtons(modal);
});

const validarCuerpo = (e: { value: string }): boolean => {
  const texto = (e.value ?? "")
    .replace(/<[^>]*>/g, "")
    .replace(/&nbsp;/g, " ")
    .trim();
  return texto.length > 0;
};

const validateForm = async (): Promise<boolean> => {
  const result = validationEngine.validateGroup("validacionComunicado");
  if (result.isValid) {
    return true;
  }
  return false;
};

const getAdjuntos = (): File[] => {
  return Object.values(ficheros.value)
    .filter((f) => f.status !== "deleted" && f.file != null)
    .map((f) => f.file!);
};

const getIdsAdjuntosExistentes = (): string[] => {
  return Object.values(ficheros.value)
    .filter((f) => f.status !== "deleted" && f.isRemote)
    .map((f) => f.id);
};

const initFicherosFromAdjuntos = (adjuntos: DetailAdjuntoComunicadoModel[]) => {
  ficheros.value = adjuntos.reduce<Ficheros>((acc, adjunto) => {
    acc[adjunto.Id] = {
      id: adjunto.Id,
      file: null,
      fileName: adjunto.Nombre,
      isRemote: true,
      remoteUri: adjunto.Ruta,
      status: "unchanged",
    };
    return acc;
  }, {});
};

const descargarAdjunto = async (fichero: Fichero) => {
  if (!fichero.remoteUri) {
    return;
  }

  try {
    const tempUrl = await api.callStandardApi("documentosGenerateTempUrl", { showLoading: true }, fichero.remoteUri);
    const response = await fetch(tempUrl);
    const blob = await response.blob();
    FileSaver.saveAs(blob, fichero.fileName ?? fichero.remoteUri.split("/").pop() ?? "adjunto");
  } catch {
    toastError(intl.i18n.t("comunicados.form.error.descargar_adjunto"));
  }
};

const guardarComunicado = async (): Promise<boolean> => {
  if (!(await validateForm())) {
    return false;
  }

  try {
    if (props.isEditMode) {
      await api.callStandardApi("editComunicado", { showLoading: true }, ...props.model.toEditApiArgs(getIdsAdjuntosExistentes(), getAdjuntos()));
    } else {
      idComunicadoCreado.value = await api.callStandardApi("createComunicado", { showLoading: true }, ...props.model.toCreateApiArgs(getAdjuntos()));
    }
    return true;
  } catch {
    toastError(intl.i18n.t("comunicados.form.error.guardar"));
    return false;
  }
};

const enviarComunicado = async (): Promise<boolean> => {
  if (!idComunicadoCreado.value) {
    return false;
  }

  try {
    await api.callStandardApi("enviarComunicado", { showLoading: true }, idComunicadoCreado.value);
    return true;
  } catch {
    toastError(intl.i18n.t("comunicados.form.error.enviar"));
    return false;
  }
};

const configureButtons = (modal: ReturnType<typeof useModalBuilder>) => {
  let saveButton: ToolbarReference<DxButton>;
  let saveAndSendButton: ToolbarReference<DxButton>;
  let cancelButton: ToolbarReference<DxButton>;

  cancelButton = modal.addButton("cancel", cancelButtonProps(), { toolbar: "bottom", location: "after" });
  saveButton = modal.addButton("save", saveButtonProps(), { toolbar: "bottom", location: "after" });
  saveAndSendButton = modal.addButton(
    "saveAndSend",
    { ...saveButtonProps(), text: intl.i18n.t("comunicados.form.guardar_y_enviar"), icon: "fa-regular fa-bullhorn" },
    { toolbar: "bottom", location: "after" },
  );

  cancelButton.update((w) => (w.onClick = () => modal.close(messageBoxResult.cancel)));
  saveButton.update(
    (w) =>
      (w.onClick = async () => {
        if (await guardarComunicado()) {
          toastSuccess(intl.i18n.t("comunicados.form.guardar_exito"));
          modal.close(messageBoxResult.ok);
        }
      }),
  );
  saveAndSendButton.update(
    (w) =>
      (w.onClick = async () => {
        if ((await guardarComunicado()) && (await enviarComunicado())) {
          toastSuccess(intl.i18n.t("comunicados.form.enviar_exito"));
          modal.close(messageBoxResult.ok);
        }
      }),
  );
};
</script>

<style lang="scss" scoped>
:deep(.contenedor-adjuntos) {
  margin: var(--spacing-xs) 0;
}

:deep(.contenedor-adjuntos .file-name) {
  max-width: 100%;
  margin-bottom: var(--spacing-2xs);
}
</style>
